import { FB_APP_ID, OG_SITE_NAME, ROBOTS } from "@config/MetaTagsConstants";
import { WEB2_BASE_URL } from "@helper/getEnvVariables";
import { truncateText } from "@helper/stringTransform";
import { ProcessedArticleData } from "@helper/transformer/useOsResponse";
import { formatDate } from "@helper/useDateFormat";
import { getAuthorsName, getTagsName } from "@routes/Article/ArticleMetaData";
import { MetaTagsProps } from "@sphtech/web2-seo-schema";
import { ProcessedArticleBody } from "@typings/Opensearch";
import type { GADataProps, PageMetaDataValues } from "@typings/PageMetaData";
import { AdsCardProps, FinishCardProps } from "@typings/QuickNews";

type CardsMetaDataProps = {
  card: ProcessedArticleData | AdsCardProps | FinishCardProps;
  onBoarded: boolean;
  pagination: number;
};

export default function QuickNewsMetaData({
  card,
  onBoarded,
  pagination,
}: CardsMetaDataProps): PageMetaDataValues {
  const isArticle =
    !("adType" in card || "quizzes" in card) && "id" in card && "title" in card;

  const cardTitle = "title" in card ? card.title : "";
  const cardId = "id" in card ? card.id : "";
  const defaultMetaTitle = "Quick News & Shorts by Tabla";
  const defaultMetaDesc = "Catch latest shorts & easy-to-read news.";
  const defaultMetaSlug = "quick-news/";

  /* -----------------------------
   * GA DATA
   * ----------------------------- */
  const gaData: GADataProps = onBoarded
    ? {
        level2: "quick-news",
        title: cardTitle,
        contentcat: 1,
        articleid: isArticle ? cardId : "",
        cue_articleid: isArticle ? cardId : "",
        contentType: "1",
        pagination: pagination + 1,
        author: "authors" in card ? getAuthorsName(card.authors || []) : "",
        pubdate:
          "createdDate" in card
            ? formatDate(card.createdDate, "yyyy-MM-dd HH:mm:ss")
            : "",
        content_publication_date_utc:
          "createdDate" in card ? card.createdDate : "",
        content_update_date_utc: "editedDate" in card ? card.editedDate : "",
        keyword: "tags" in card ? getTagsName(card.tags || []) : "",
      }
    : {
        level2: "quick-news",
        title: "quick_news_index",
        contentcat: 1,
        contentType: "1",
        pagination: 0,
      };

  /* -----------------------------
   * META TITLE
   * ----------------------------- */
  const metaTitle = onBoarded ? cardTitle : defaultMetaTitle;

  /* -----------------------------
   * META DESCRIPTION
   * ----------------------------- */
  const generatedMetaDesc = getMetaDescription(card);
  const metaDesc =
    onBoarded && generatedMetaDesc
      ? `${generatedMetaDesc}...`
      : defaultMetaDesc;

  /* -----------------------------
   * META SLUG
   * ----------------------------- */
  const metaSlug = onBoarded ? `quick-news/${cardId}` : defaultMetaSlug;

  /* -----------------------------
   * META Image
   * ----------------------------- */
  const metaImage = "imageField" in card ? (card.imageField?.url ?? "") : "";

  const additionalMetaDescriptors = [
    { tagName: "link", rel: "manifest", href: "/manifest.json" },
  ];

  const metaTags: MetaTagsProps = {
    title: metaTitle,
    description: metaDesc,
    slug: metaSlug,
    image: metaImage || undefined,
    robots: ROBOTS,
    fbAppId: FB_APP_ID,
    urlOrigin: WEB2_BASE_URL,
    ogTitle: metaTitle,
    ogDescription: metaDesc,
    ogSiteName: OG_SITE_NAME,
    additionalMetaDescriptors,
  };

  return {
    gaData,
    metaTags,
  };
}

/* -----------------------------------------------------
 * Helper: Description builder
 * ----------------------------------------------------- */
function getMetaDescription(
  card: ProcessedArticleData | AdsCardProps | FinishCardProps,
): string {
  if ("articleBody" in card) {
    const text = getFirstTwoLines(card.articleBody);
    const metaText = truncateText(text, 110);
    if (metaText) return metaText;
  }

  return "";
}

/* -----------------------------------------------------
 * Helper: Get first two lines of article
 * ----------------------------------------------------- */
export function getFirstTwoLines(
  articleBody: ProcessedArticleBody | null,
): string {
  if (!articleBody) return "";

  const paragraphLines = articleBody
    .filter((el) => el.type === "paragraph")
    .flatMap((para) => (para.value ? para.value.split("\n") : []));

  return paragraphLines.slice(0, 2).join("\n");
}
